(function () {
  'use strict';

  class ScrollAccordion {
    constructor(section) {
      this.section    = section;
      this.trigger    = section.querySelector('[data-scroll-accordion-trigger]');
      this.container  = section.querySelector('[data-scroll-accordion-container]');
      this.content    = section.querySelector('[data-scroll-accordion-content]');
      this.fullHeight = 0;
      this.animating  = false;
      this.isOpen     = false;

      if (!this.trigger || !this.container || !this.content) return;
      this.init();
    }

    init() {
      this.trigger.addEventListener('click', () => this.onTriggerClick());
      this.measureHeight();
      window.addEventListener('load',   () => this.measureHeight(), { once: true });
      window.addEventListener('resize', () => this.measureHeight(), { passive: true });
    }

    // ── Measurement ───────────────────────────────────────────────────────

    measureHeight() {
      // Briefly expand and clear transform to get the natural content height
      const prevTransform = this.content.style.transform;
      this.container.style.height = 'auto';
      this.content.style.transform = '';
      this.fullHeight = this.content.offsetHeight;

      // Restore without causing a visible jump
      if (this.isOpen) {
        this.container.style.height = `${this.fullHeight}px`;
        this.content.style.transform = prevTransform;
      } else {
        this.container.style.height = '0px';
        this.content.style.transform = '';
      }
    }

    // ── Manual open / close ───────────────────────────────────────────────

    onTriggerClick() {
      if (this.trigger.classList.contains('is-expanded')) {
        this._animate('close');
      } else {
        this._animate('open');
      }
    }

    _animate(direction) {
      const DURATION = 500;
      const ease     = `height ${DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`;
      this.animating = true;
      this.content.style.transform = ''; // always clear transform before a height animation

      if (direction === 'close') {
        this.container.style.transition = 'none';
        this.container.style.height = `${this.fullHeight}px`;
        this.container.classList.remove('is-expanded');
        this.trigger.classList.remove('is-expanded');
        this.trigger.setAttribute('aria-expanded', 'false');

        requestAnimationFrame(() => requestAnimationFrame(() => {
          this.container.style.transition = ease;
          this.container.style.height = '0px';
          setTimeout(() => {
            this.isOpen = false;
            this.container.style.transition = '';
            this.animating = false;
          }, DURATION);
        }));
      } else {
        this.isOpen = true;
        this.container.style.transition = ease;
        this.container.style.height = `${this.fullHeight}px`;
        this.trigger.classList.add('is-expanded');
        this.trigger.setAttribute('aria-expanded', 'true');

        setTimeout(() => {
          this.container.classList.add('is-expanded');
          this.container.style.height = 'auto';
          this.container.style.transition = '';
          this.animating = false;
        }, DURATION);
      }
    }
  }

  /**
   * ProductAccordionVariants
   * Handles variant switching inside a [data-product-accordion] element.
   * Reads variant data from an embedded JSON script tag, swaps the image,
   * and updates the hidden ATC form input when an option button is clicked.
   */
  class ProductAccordionVariants {
    constructor(section) {
      this.section = section;
      const jsonEl = section.querySelector('[data-product-json]');
      if (!jsonEl) return;

      try {
        this.productData = JSON.parse(jsonEl.textContent);
      } catch (e) {
        console.warn('ScrollAccordion: could not parse product JSON', e);
        return;
      }

      this.variantIdInput = section.querySelector('[data-variant-id]');
      this.productImageEl = section.querySelector('[data-product-image]');
      this.optionButtons  = section.querySelectorAll('[data-variant-option]');
      this.selectedOptions = [];
      this.currentVariant  = null;

      this.init();
    }

    init() {
      const initial =
        this.productData.variants.find((v) => v.available) ||
        this.productData.variants[0];
      if (!initial) return;

      this.currentVariant  = initial;
      this.selectedOptions = [...initial.options];
      this.updateActiveButtons();

      this.optionButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.onOptionClick(btn));
      });
    }

    onOptionClick(button) {
      const index = parseInt(button.dataset.optionIndex, 10);
      const value = button.dataset.optionValue;

      this.selectedOptions[index] = value;

      const match = this.productData.variants.find((v) =>
        v.options.every((opt, i) => opt === this.selectedOptions[i])
      );

      if (!match) return;

      this.currentVariant = match;
      this.updateActiveButtons();
      this.updateImage(match);
      this.updateVariantId(match);
    }

    updateActiveButtons() {
      this.optionButtons.forEach((btn) => {
        const idx    = parseInt(btn.dataset.optionIndex, 10);
        const active = btn.dataset.optionValue === this.selectedOptions[idx];
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
      });
    }

    updateImage(variant) {
      if (!this.productImageEl) return;
      const img = this.productImageEl.querySelector('img');
      if (!img) return;
      const src = variant.metafield_image || (variant.featured_image && variant.featured_image.src);
      if (!src) return;
      img.src    = src;
      img.srcset = '';
    }

    updateVariantId(variant) {
      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
      }
    }
  }

  // ── Bootstrap ────────────────────────────────────────────────────────────

  function init() {
    document
      .querySelectorAll('[data-scroll-accordion]:not([data-sa-ready])')
      .forEach((el) => {
        el.setAttribute('data-sa-ready', '');
        new ScrollAccordion(el);
      });

    document
      .querySelectorAll('[data-product-accordion]:not([data-pa-ready])')
      .forEach((el) => {
        el.setAttribute('data-pa-ready', '');
        new ProductAccordionVariants(el);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when Shopify reloads a section in the theme editor
  document.addEventListener('shopify:section:load', init);
})();
